import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  IconButton as MuiIconButton,
  Stack,
  TextField,
  Typography,
  Button as MuiButton,
  Tooltip,
  TableCell,
  TableRow,
  Toolbar,
  FormHelperText,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

import {
  Add,
  DeleteOutline,
  QrCode2,
  Save,
  Replay,
  ContentCopy,
  Close,
  OpenInNew,
} from '@mui/icons-material';

import QRCode from 'qrcode.react';

import IconButton from '@components/common/IconButton';
import { usePageHeader } from '@components/common/pageHeader.context';

import { EsimEventType, EsimStatus, EsimType } from '@models/stock';

import {
  addEsimInventoryMemo,
  deleteEsimInventoryMemo,
  editEsimInventory,
  type EsimInventoryResponse,
  type EsimInventoryEventResponse,
} from '@apis/esim';

import { useInventoryDetail } from '@queries/inventories/useInventoryDetail';
import { useInventoryEvents } from '@queries/inventories/useInventoryEvents';

import { dateToTimestamp, timestampToDate } from '@utils/time';
import useAsyncEffect from '@hooks/useAsyncEffect';

import { useConfirm } from '@components/common/confirm/confirm.context';
import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';

import { StandardTableFrame, cellSx } from '@components/common/table';

const dashIfEmpty = (v: unknown) => {
  if (v === null || v === undefined) return '-';
  if (typeof v === 'string') return v.trim() ? v : '-';
  return String(v);
};

const isNonEmptyString = (v: unknown): v is string =>
  typeof v === 'string' && v.trim().length > 0;

/**
 * Validates YYYY-MM-DD and rejects non-existing dates (e.g., 2026-02-31).
 */
const isValidYmd = (v: string) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const t = Date.parse(v);
  return Number.isFinite(t);
};

/**
 * User-friendly event type labels.
 */
const ESIM_EVENT_TYPE_UI: Record<EsimEventType, { label: string }> = {
  [EsimEventType.UPLOADED]: { label: 'Uploaded' },
  [EsimEventType.STATE_CHANGED]: { label: 'State changed' },
  [EsimEventType.EXPIRE_DATE_CHANGED]: { label: 'Expiry date changed' },
  [EsimEventType.USER_MEMO]: { label: 'Memo' },
};

const renderEventTypeLabel = (t: EsimEventType | undefined) => {
  if (!t) return '-';
  return ESIM_EVENT_TYPE_UI[t]?.label ?? String(t);
};

/**
 * User-friendly inventory status chips.
 */
const ESIM_STATUS_UI: Record<
  EsimStatus,
  {
    label: string;
    color: 'default' | 'primary' | 'warning' | 'error';
    variant: 'filled' | 'outlined';
  }
> = {
  [EsimStatus.READY]: { label: 'Ready', color: 'primary', variant: 'filled' },
  [EsimStatus.SHIPPED]: { label: 'Shipped', color: 'default', variant: 'outlined' },
  [EsimStatus.PENDING]: { label: 'Pending', color: 'warning', variant: 'outlined' },
  [EsimStatus.EXPIRED]: { label: 'Expired', color: 'warning', variant: 'filled' },
  [EsimStatus.CANCELED]: { label: 'Canceled', color: 'error', variant: 'filled' },
};

const renderStatusChip = (status: EsimStatus | undefined) => {
  const s = status ?? EsimStatus.READY;
  const meta =
    ESIM_STATUS_UI[s] ?? ({ label: String(s), color: 'default', variant: 'outlined' } as const);

  return (
    <Chip
      size="small"
      label={meta.label}
      color={meta.color}
      variant={meta.variant}
      sx={{ fontWeight: 800 }}
    />
  );
};

type UpdateBaseline = { esimStatus: EsimStatus; expireAt: number };

const EsimInventoryDetail = () => {
  const { esimInventoryId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setPageHeaderSlots } = usePageHeader();

  const confirm = useConfirm();
  const systemAlert = useSystemAlert();
  const queryClient = useQueryClient();

  const esimInventoryIdNumber = useMemo(() => {
    const n = Number(esimInventoryId);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [esimInventoryId]);

  /**
   * Inventory detail data shown in read-only fields.
   * Only status and expiry are editable in the Update section.
   */
  const [form, setForm] = useState<EsimInventoryResponse>({
    esimInventoryId: esimInventoryIdNumber,
    redemptionCode: '',
    lpaUrl: '',
    confirmationCode: '',
    serialNo: '',
    esimType: EsimType.DISCOVERY,
    esimStatus: EsimStatus.READY,
    expireAt: 0,
    stock: { stockId: 0, stockCode: '', stockName: '', esimType: EsimType.DISCOVERY },
    iccid: '',
    imsi: '',
    msisdn: '',
    pin: '',
    puk: '',
  });

  /**
   * Baseline for dirty-check (only for status + expiry).
   */
  const [baselineUpdate, setBaselineUpdate] = useState<UpdateBaseline | null>(null);

  /**
   * Expiry draft is kept as YYYY-MM-DD for user input, then converted into timestamp for API.
   */
  const [expiryYmdDraft, setExpiryYmdDraft] = useState<string>('');
  const [expiryError, setExpiryError] = useState<string>('');

  const [memo, setMemo] = useState('');

  /**
   * QR dialog for Activation Code (LPA URL).
   */
  const [qrOpen, setQrOpen] = useState(false);

  const {
    data: esimInventoryDetail,
    refetch,
    isLoading: isLoadingDetail,
  } = useInventoryDetail(esimInventoryIdNumber);

  const {
    data: esimInventoryEventsData,
    refetch: refetchInventoryEventsData,
    isLoading: isLoadingEvents,
  } = useInventoryEvents(esimInventoryIdNumber);

  /**
   * Load detail into local state and initialize baseline + expiry draft.
   */
  useAsyncEffect(async () => {
    const detail = esimInventoryDetail?.data?.[0];
    if (!esimInventoryIdNumber || !detail) return;

    setForm(detail);
    setBaselineUpdate({ esimStatus: detail.esimStatus, expireAt: detail.expireAt });

    const ymd = detail.expireAt ? timestampToDate(detail.expireAt, 'YYYY-MM-DD') || '' : '';
    setExpiryYmdDraft(ymd);
    setExpiryError(ymd ? '' : 'Expiry Date is required.');
  }, [esimInventoryDetail, esimInventoryIdNumber]);

  /**
   * If user navigated from list page, keep the exact list URL for a reliable "Back to list".
   * (This covers header back button use-cases beyond browser back.)
   */
  const from = useMemo(() => {
    const s = (location.state as any)?.from;
    return typeof s === 'string' && s.trim() ? s : undefined;
  }, [location.state]);

  const handleBackToList = useCallback(() => {
    if (from) navigate(from);
    else navigate(-1);
  }, [from, navigate]);

  /**
   * Page header title uses Stock Code.
   */
  useEffect(() => {
    const stockCode = form?.stock?.stockCode;
    setPageHeaderSlots({
      leading: { title: stockCode ? `Stock Code: ${stockCode}` : undefined },
    });

    // PageHeaderProvider usually resets slots on navigation,
    // but we clear it defensively for predictable behavior.
    return () => setPageHeaderSlots({});
  }, [form?.stock?.stockCode, handleBackToList, setPageHeaderSlots]);

  const hasLpaUrl = useMemo(() => isNonEmptyString(form.lpaUrl), [form.lpaUrl]);

  const handleOpenQr = useCallback(() => setQrOpen(true), []);
  const handleCloseQr = useCallback(() => setQrOpen(false), []);

  /**
   * Copy helper for read-only fields.
   */
  const handleCopy = useCallback(
    async (label: string, value: unknown) => {
      const text = typeof value === 'string' ? value.trim() : '';
      if (!text) return;

      try {
        await navigator.clipboard.writeText(text);
        systemAlert.success(`${label} copied.`);
      } catch {
        systemAlert.error('Failed to copy.');
      }
    },
    [systemAlert],
  );

  /**
   * Reusable end-adornment icon for copy actions.
   */
  const CopyIconAdornment = useCallback(
    ({ label, value }: { label: string; value: unknown }) => {
      const enabled = isNonEmptyString(value);

      return (
        <Tooltip title={enabled ? `Copy ${label}` : 'No value'}>
          <span>
            <MuiIconButton
              size="small"
              edge="end"
              disabled={!enabled}
              onClick={() => handleCopy(label, value)}
            >
              <ContentCopy fontSize="small" />
            </MuiIconButton>
          </span>
        </Tooltip>
      );
    },
    [handleCopy],
  );

  /* ------------------------------------------------------------------ */
  /* Update section handlers (status + expiry)                           */
  /* ------------------------------------------------------------------ */

  const handleChangeStatus = useCallback((next: EsimStatus) => {
    setForm((prev) => ({ ...prev, esimStatus: next }));
  }, []);

  const handleChangeExpiryDraft = useCallback((nextYmd: string) => {
    setExpiryYmdDraft(nextYmd);

    if (!nextYmd) {
      setExpiryError('Expiry Date is required.');
      return;
    }

    // Fixes "unused function" warning by using the validator here.
    if (!isValidYmd(nextYmd)) {
      setExpiryError('Invalid date. Use YYYY-MM-DD.');
      return;
    }

    try {
      const nextExpireAt = dateToTimestamp(nextYmd, false, true, true);
      setForm((prev) => ({ ...prev, expireAt: nextExpireAt }));
      setExpiryError('');
    } catch {
      setExpiryError('Invalid date.');
    }
  }, []);

  /**
   * Preview compares current draft vs baseline to show user what will change.
   */
  const preview = useMemo(() => {
    if (!baselineUpdate) return null;

    const baselineStatus = baselineUpdate.esimStatus;
    const nextStatus = form.esimStatus;

    const baselineExpiryYmd = baselineUpdate.expireAt
      ? timestampToDate(baselineUpdate.expireAt, 'YYYY-MM-DD') || '-'
      : '-';

    const nextExpiryYmd = form.expireAt
      ? timestampToDate(form.expireAt, 'YYYY-MM-DD') || '-'
      : '-';

    return {
      statusChanged: baselineStatus !== nextStatus,
      expiryChanged: baselineUpdate.expireAt !== form.expireAt,
      baselineStatus,
      nextStatus,
      baselineExpiryYmd,
      nextExpiryYmd,
    };
  }, [baselineUpdate, form.esimStatus, form.expireAt]);

  /**
   * "Dirty" means (1) baseline exists, (2) draft date is valid, and (3) at least one field differs.
   */
  const isDirty = useMemo(() => {
    if (!baselineUpdate) return false;
    if (!expiryYmdDraft || Boolean(expiryError)) return false;

    return (
      baselineUpdate.esimStatus !== form.esimStatus ||
      baselineUpdate.expireAt !== form.expireAt
    );
  }, [baselineUpdate, form.esimStatus, form.expireAt, expiryYmdDraft, expiryError]);

  const handleResetUpdate = useCallback(() => {
    if (!baselineUpdate) return;

    setForm((prev) => ({
      ...prev,
      esimStatus: baselineUpdate.esimStatus,
      expireAt: baselineUpdate.expireAt,
    }));

    const ymd = baselineUpdate.expireAt
      ? timestampToDate(baselineUpdate.expireAt, 'YYYY-MM-DD') || ''
      : '';

    setExpiryYmdDraft(ymd);
    setExpiryError(ymd ? '' : 'Expiry Date is required.');
  }, [baselineUpdate]);

  const handleUpdate = useCallback(async () => {
    if (!esimInventoryIdNumber) {
      systemAlert.error('Invalid inventory id.');
      return;
    }
    if (!baselineUpdate) return;

    if (!expiryYmdDraft) {
      setExpiryError('Expiry Date is required.');
      return;
    }
    if (expiryError) return;
    if (!isDirty) return;

    const ok = await confirm({
      priority: 'info',
      title: 'Update this inventory?',
      description: 'This updates only State and Expiry Date.',
      confirmText: 'Update',
      cancelText: 'Cancel',
      autoFocusConfirm: false,
    });

    if (!ok) return;

    try {
      await editEsimInventory({
        esimInventoryId: esimInventoryIdNumber,
        esimStatus: form.esimStatus,
        expireAt: form.expireAt,
      });

      // Keep UI consistent after update by reloading both detail and event history.
      await Promise.all([refetch(), refetchInventoryEventsData()]);

      // Invalidate list + count so that going back shows updated status without manual refresh.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['esimInventories'] }),
        queryClient.invalidateQueries({ queryKey: ['esimInventoriesCount'] }),
      ]);

      // This guarantees the cache is updated BEFORE you go back.
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['esimInventories'], type: 'all' }),
        queryClient.refetchQueries({ queryKey: ['esimInventoriesCount'], type: 'all' }),
      ]);

      setBaselineUpdate({ esimStatus: form.esimStatus, expireAt: form.expireAt });

      const ymd = form.expireAt
        ? timestampToDate(form.expireAt, 'YYYY-MM-DD') || ''
        : '';

      setExpiryYmdDraft(ymd);
      setExpiryError(ymd ? '' : 'Expiry Date is required.');

      systemAlert.success('Inventory updated.');
    } catch {
      systemAlert.error('Failed to update inventory.');
    }
  }, [
    confirm,
    esimInventoryIdNumber,
    baselineUpdate,
    form.esimStatus,
    form.expireAt,
    expiryYmdDraft,
    expiryError,
    isDirty,
    refetch,
    refetchInventoryEventsData,
    systemAlert,
    queryClient,
  ]);

  /* ------------------------------------------------------------------ */
  /* Memo actions                                                       */
  /* ------------------------------------------------------------------ */

  const handleAddMemo = useCallback(async () => {
    if (!esimInventoryIdNumber) {
      systemAlert.error('Invalid inventory id.');
      return;
    }

    const trimmed = memo.trim();
    if (!trimmed) return;

    try {
      await addEsimInventoryMemo(esimInventoryIdNumber, trimmed);
      await refetchInventoryEventsData();
      setMemo('');
      systemAlert.success('Memo added.');
    } catch {
      systemAlert.error('Failed to add memo.');
    }
  }, [esimInventoryIdNumber, memo, refetchInventoryEventsData, systemAlert]);

  const handleDeleteMemo = useCallback(
    async (esimInventoryEventId: number) => {
      if (!esimInventoryIdNumber) {
        systemAlert.error('Invalid inventory id.');
        return;
      }

      const ok = await confirm({
        priority: 'danger',
        title: 'Delete this memo?',
        description: 'This action cannot be undone.',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        autoFocusConfirm: false,
      });

      if (!ok) return;

      try {
        await deleteEsimInventoryMemo(esimInventoryIdNumber, esimInventoryEventId);
        await refetchInventoryEventsData();
        systemAlert.success('Memo deleted.');
      } catch {
        systemAlert.error('Failed to delete memo.');
      }
    },
    [confirm, esimInventoryIdNumber, refetchInventoryEventsData, systemAlert],
  );

  /**
   * Sort events by createdAt DESC without mutating the original array.
   */
  const events: EsimInventoryEventResponse[] = useMemo(() => {
    const list = (esimInventoryEventsData?.data ?? []) as EsimInventoryEventResponse[];
    return [...list].sort((a, b) => b.createdAt - a.createdAt);
  }, [esimInventoryEventsData]);

  const latestEventId = useMemo(() => events[0]?.esimInventoryEventId ?? null, [events]);

  return (
    <>
      <Box mt={2}>
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardHeader
            title={
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" fontWeight={900}>
                  {dashIfEmpty(form.stock?.stockName)}
                </Typography>
                <Chip
                  size="small"
                  label={dashIfEmpty(form.esimType)}
                  variant="outlined"
                  sx={{ fontWeight: 800 }}
                />
              </Stack>
            }
            subheader={
              <Typography variant="body2" color="text.secondary">
                Stock Code: {dashIfEmpty(form.stock?.stockCode)}
              </Typography>
            }
          />

          <Divider />

          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={3} lg={2}>
                <TextField
                  label="Redeem Code"
                  value={dashIfEmpty(form.redemptionCode)}
                  fullWidth
                  size="small"
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <CopyIconAdornment
                          label="Redeem Code"
                          value={form.redemptionCode}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={8}>
                <TextField
                  label="Activation Code"
                  value={dashIfEmpty(form.lpaUrl)}
                  fullWidth
                  size="small"
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Stack direction="row" spacing={0.25} alignItems="center">
                          <CopyIconAdornment
                            label="Activation Code"
                            value={form.lpaUrl}
                          />

                          <Tooltip
                            title={
                              hasLpaUrl
                                ? 'Show QR (Activation Code)'
                                : 'Activation Code is empty'
                            }
                          >
                            <span>
                              <MuiIconButton
                                onClick={handleOpenQr}
                                disabled={!hasLpaUrl}
                                edge="end"
                                size="small"
                              >
                                <QrCode2 fontSize="small" />
                              </MuiIconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={3} lg={2}>
                <TextField
                  label="Confirm Code"
                  value={dashIfEmpty(form.confirmationCode)}
                  fullWidth
                  size="small"
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <CopyIconAdornment
                          label="Confirm Code"
                          value={form.confirmationCode}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={4} lg={2}>
                <TextField
                  label="Serial No"
                  value={dashIfEmpty(form.serialNo)}
                  fullWidth
                  size="small"
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <CopyIconAdornment
                          label="Serial No"
                          value={form.serialNo}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={4} lg={2}>
                <TextField
                  label="ICCID"
                  value={dashIfEmpty(form.iccid)}
                  fullWidth
                  size="small"
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <CopyIconAdornment label="ICCID" value={form.iccid} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={4} lg={2}>
                <TextField
                  label="IMSI"
                  value={dashIfEmpty(form.imsi)}
                  fullWidth
                  size="small"
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <CopyIconAdornment label="IMSI" value={form.imsi} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={4} lg={2}>
                <TextField
                  label="MSISDN"
                  value={dashIfEmpty(form.msisdn)}
                  fullWidth
                  size="small"
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <CopyIconAdornment
                          label="MSISDN"
                          value={form.msisdn}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  label="PIN"
                  value={dashIfEmpty(form.pin)}
                  fullWidth
                  size="small"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  label="PUK"
                  value={dashIfEmpty(form.puk)}
                  fullWidth
                  size="small"
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>
          </CardContent>

          <Divider />

          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="State"
                  value={form.esimStatus}
                  onChange={(e) => handleChangeStatus(e.target.value as EsimStatus)}
                  fullWidth
                  size="small"
                  SelectProps={{ native: true }}
                >
                  {Object.values(EsimStatus).map((s) => (
                    <option key={s} value={s}>
                      {ESIM_STATUS_UI[s]?.label ?? s}
                    </option>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Expiry Date"
                  type="date"
                  value={expiryYmdDraft}
                  onChange={(e) => handleChangeExpiryDraft(e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(expiryError)}
                />
                {expiryError ? (
                  <FormHelperText error>{expiryError}</FormHelperText>
                ) : null}
              </Grid>

              {preview && (preview.statusChanged || preview.expiryChanged) ? (
                <Grid item xs={12}>
                  <Box
                    sx={(theme) => ({
                      mt: 0.5,
                      borderRadius: 2,
                      p: 1.5,
                      border: `1px solid ${theme.palette.divider}`,
                      bgcolor:
                        theme.palette.mode === 'light'
                          ? theme.palette.grey[50]
                          : theme.palette.grey[900],
                    })}
                  >
                    <Typography variant="subtitle2" fontWeight={900} gutterBottom>
                      Preview
                    </Typography>

                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          State:
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {renderStatusChip(preview.baselineStatus)}
                          <Typography variant="body2" color="text.secondary">
                            →
                          </Typography>
                          {renderStatusChip(preview.nextStatus)}
                        </Stack>
                      </Stack>

                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          Expiry:
                        </Typography>
                        <Typography variant="body2" fontWeight={800}>
                          {preview.baselineExpiryYmd}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          →
                        </Typography>
                        <Typography variant="body2" fontWeight={800}>
                          {preview.nextExpiryYmd}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>
                </Grid>
              ) : null}

              <Grid item xs={12}>
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="flex-end"
                  alignItems="center"
                >
                  {baselineUpdate &&
                  (baselineUpdate.esimStatus !== form.esimStatus ||
                    baselineUpdate.expireAt !== form.expireAt) ? (
                    <Tooltip title="Revert changes">
                      <span>
                        <MuiButton
                          variant="outlined"
                          startIcon={<Replay />}
                          onClick={handleResetUpdate}
                          disabled={!baselineUpdate || Boolean(isLoadingDetail)}
                        >
                          Reset
                        </MuiButton>
                      </span>
                    </Tooltip>
                  ) : null}

                  <MuiButton
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleUpdate}
                    disabled={!esimInventoryIdNumber || Boolean(isLoadingDetail) || !isDirty}
                  >
                    Update
                  </MuiButton>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      <Box mt={3}>
        <Typography variant="subtitle1" fontWeight={800}>
          Memos
        </Typography>

        <Box mt={1} mb={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              label="Add memo"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              fullWidth
              size="small"
              placeholder="Write a memo…"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddMemo();
                }
              }}
            />
            <Tooltip title="Add memo">
              <span>
                <MuiIconButton
                  onClick={handleAddMemo}
                  disabled={!memo.trim() || !esimInventoryIdNumber}
                  color="primary"
                >
                  <Add />
                </MuiIconButton>
              </span>
            </Tooltip>
          </Stack>
        </Box>

        <StandardTableFrame<EsimInventoryEventResponse>
          containerSx={{ borderRadius: 2 }}
          minWidth={1100}
          columnsCount={7}
          isLoading={Boolean(isLoadingEvents)}
          loadingVariant="skeleton"
          skeletonRows={6}
          emptyText="No memos found."
          head={
            <TableRow>
              <TableCell width={70} align="right">
                #
              </TableCell>
              <TableCell width={140} sx={cellSx.nowrap}>
                Owner
              </TableCell>
              <TableCell width={160} sx={cellSx.nowrap}>
                Event Type
              </TableCell>
              <TableCell sx={{ minWidth: 360 }}>Description</TableCell>
              <TableCell width={90} align="center" sx={cellSx.nowrap}>
                Order
              </TableCell>
              <TableCell width={200} sx={cellSx.nowrap}>
                Date &amp; Time
              </TableCell>
              <TableCell width={90} align="center" sx={cellSx.nowrap}>
                Delete
              </TableCell>
            </TableRow>
          }
          rows={events}
          getRowKey={(row) => String(row.esimInventoryEventId)}
          renderRow={(event, index) => {
            const orderId = event.orderId;
            const hasOrder = typeof orderId === 'number' && Number.isFinite(orderId);
            const isLatest = latestEventId !== null && event.esimInventoryEventId === latestEventId;

            const latestCellSx = isLatest
              ? (theme: any) => ({
                  bgcolor:
                    theme.palette.mode === 'light'
                      ? theme.palette.action.hover
                      : theme.palette.action.selected,
                })
              : undefined;

            return (
              <>
                <TableCell
                  align="right"
                  sx={{
                    ...cellSx.number,
                    ...(latestCellSx ? latestCellSx : null),
                    fontWeight: isLatest ? 900 : undefined,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                    {isLatest ? (
                      <Chip
                        size="small"
                        label="Latest"
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 900 }}
                      />
                    ) : null}
                    <span>{index + 1}</span>
                  </Stack>
                </TableCell>

                <TableCell
                  sx={{
                    ...cellSx.nowrap,
                    ...(latestCellSx ? latestCellSx : null),
                    fontWeight: isLatest ? 900 : undefined,
                  }}
                >
                  {dashIfEmpty(event.owner)}
                </TableCell>

                <TableCell sx={{ ...cellSx.nowrap, ...(latestCellSx ? latestCellSx : null) }}>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Typography variant="body2" fontWeight={800}>
                      {renderEventTypeLabel(event.eventType)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      (
                      <Tooltip title={String(event.eventType)}>
                        <span>{String(event.eventType)}</span>
                      </Tooltip>
                      )
                    </Typography>
                  </Stack>
                </TableCell>

                <TableCell sx={{ ...(latestCellSx ? latestCellSx : null) }}>
                  <Typography
                    variant="body2"
                    sx={{ wordBreak: 'break-word' }}
                    fontWeight={isLatest ? 800 : undefined}
                  >
                    {dashIfEmpty(event.description)}
                  </Typography>
                </TableCell>

                <TableCell align="center" sx={{ ...cellSx.nowrap, ...(latestCellSx ? latestCellSx : null) }}>
                  {hasOrder ? (
                    <Tooltip title="Open order detail">
                      <span>
                        <MuiIconButton size="small" onClick={() => navigate(`/orders/${orderId}`)}>
                          <OpenInNew fontSize="small" />
                        </MuiIconButton>
                      </span>
                    </Tooltip>
                  ) : (
                    '-'
                  )}
                </TableCell>

                <TableCell
                  sx={{
                    ...cellSx.nowrap,
                    ...(latestCellSx ? latestCellSx : null),
                    fontWeight: isLatest ? 800 : undefined,
                  }}
                >
                  {dashIfEmpty(timestampToDate(event.createdAt))}
                </TableCell>

                <TableCell align="center" sx={{ ...cellSx.nowrap, ...(latestCellSx ? latestCellSx : null) }}>
                  <IconButton onClick={() => handleDeleteMemo(event.esimInventoryEventId)}>
                    <DeleteOutline />
                  </IconButton>
                </TableCell>
              </>
            );
          }}
        />
      </Box>

      <Toolbar />

      <Dialog
        open={qrOpen}
        onClose={handleCloseQr}
        maxWidth="xs"
        fullWidth
        aria-labelledby="lpa-qr-dialog-title"
      >
        <DialogTitle id="lpa-qr-dialog-title" sx={{ pr: 6, fontWeight: 900 }}>
          eSIM QR Code
          <MuiIconButton
            onClick={handleCloseQr}
            size="small"
            sx={{ position: 'absolute', right: 8, top: 8 }}
            aria-label="Close"
          >
            <Close fontSize="small" />
          </MuiIconButton>
        </DialogTitle>

        <DialogContent>
          <Box
            sx={(theme) => ({
              mt: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 2,
              p: 2,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor:
                theme.palette.mode === 'light'
                  ? theme.palette.grey[50]
                  : theme.palette.grey[900],
            })}
          >
            <QRCode value={(form.lpaUrl ?? '').trim()} size={320} />
          </Box>

          <Box mt={2}>
            <TextField
              label="Activation Code"
              value={dashIfEmpty(form.lpaUrl)}
              fullWidth
              size="small"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <CopyIconAdornment label="Activation Code" value={form.lpaUrl} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {isNonEmptyString(form.confirmationCode) ? (
            <Box mt={2}>
              <TextField
                label="Confirmation Code"
                value={form.confirmationCode.trim()}
                fullWidth
                size="small"
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <CopyIconAdornment
                        label="Confirmation Code"
                        value={form.confirmationCode}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MuiButton onClick={handleCloseQr} variant="outlined">
            Close
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EsimInventoryDetail;
